import type { CustomPlacement, MockupTemplate, Point, ScreenWarpMode } from '../types';
import { getCategoryAspect } from '../categoryAspect';
import { fetchImage, createCanvas } from './loadImage';
import { warpImageToQuad } from './warp';
import { applyMask } from './mask';

export interface LoadedMockupAssets {
  template: MockupTemplate;
  base: CanvasImageSource;
  mask: CanvasImageSource;
  mask2: CanvasImageSource | null;
  overlay: CanvasImageSource;
  backgroundMask: CanvasImageSource | null;
  reflectionMask: CanvasImageSource | null;
  reflectionBlurMask: CanvasImageSource | null;
  /** B&W mask applied to the user's screen content: white = maximum blur, black = no blur. */
  screenBlurMask: CanvasImageSource | null;
}

export interface DiffuseAdjustments {
  hue: number;
  saturation: number;
  brightness: number;
  contrast: number;
  monochrome: boolean;
}

interface ImageSourceRect {
  sx: number;
  sy: number;
  sw: number;
  sh: number;
}

interface ImageDestRect {
  dx: number;
  dy: number;
  dw: number;
  dh: number;
}

export interface BackgroundAdjustments {
  transparent: boolean;
  mode: 'original' | 'tint' | 'tritone';
  shadowColor: string;
  midColor: string;
  highlightColor: string;
}

export interface ReflectionAdjustments {
  enabled: boolean;
  opacity: number;
  blur: number;
}

export async function loadMockupAssets(
  template: MockupTemplate,
  variant: 'preview' | 'export' = 'export'
): Promise<LoadedMockupAssets> {
  const baseUrl = variant === 'preview' ? template.editBase : template.base;
  const maskUrl = variant === 'preview' ? template.editMask : template.mask;
  const mask2Url = variant === 'preview' ? template.editMask2 : template.mask2;
  const overlayUrl = variant === 'preview' ? template.editOverlay : template.overlay;
  const backgroundMaskUrl = variant === 'preview' ? template.editBackgroundMask : template.backgroundMask;
  const reflectionMaskUrl = variant === 'preview' ? template.editReflectionMask : template.reflectionMask;
  const reflectionBlurMaskUrl =
    variant === 'preview' ? template.editReflectionBlurMask : template.reflectionBlurMask;
  const screenBlurMaskUrl =
    variant === 'preview'
      ? template.editScreenBlurMask || template.screenBlurMask
      : template.screenBlurMask;

  if (variant === 'preview' && (!baseUrl || !maskUrl || !overlayUrl)) {
    throw new Error('Preview assets are missing for this template.');
  }

  const [base, mask, mask2, overlay, backgroundMask, reflectionMask, reflectionBlurMask, screenBlurMask] = await Promise.all([
    fetchImage(baseUrl),
    fetchImage(maskUrl),
    mask2Url ? fetchImage(mask2Url).catch(() => null) : Promise.resolve(null),
    fetchImage(overlayUrl),
    backgroundMaskUrl ? fetchImage(backgroundMaskUrl).catch(() => null) : Promise.resolve(null),
    reflectionMaskUrl ? fetchImage(reflectionMaskUrl).catch(() => null) : Promise.resolve(null),
    reflectionBlurMaskUrl ? fetchImage(reflectionBlurMaskUrl).catch(() => null) : Promise.resolve(null),
    screenBlurMaskUrl ? fetchImage(screenBlurMaskUrl).catch(() => null) : Promise.resolve(null)
  ]);

  return {
    template,
    base,
    mask,
    mask2,
    overlay,
    backgroundMask,
    reflectionMask,
    reflectionBlurMask,
    screenBlurMask
  };
}

export function renderMaster(
  mockup: LoadedMockupAssets,
  userImage: CanvasImageSource | null,
  options: {
    gridX?: number;
    gridY?: number;
    diffuse?: DiffuseAdjustments;
    background?: BackgroundAdjustments;
    reflection?: ReflectionAdjustments;
    useAlternateMask?: boolean;
    customPlacement?: CustomPlacement | null;
    warpMode?: ScreenWarpMode;
    renderSize?: number;
    screenRenderScale?: number;
    screenQuadOverride?: Point[];
    reflectionQuadOverride?: Point[];
    /** When true the screen blur mask is skipped for performance (realtime playback). */
    realtime?: boolean;
  } = {}
): HTMLCanvasElement {
  const outputSize = mockup.template.outputSize || 1920;
  const size = Math.max(1, Math.round(options.renderSize || outputSize));
  const screenRenderScale = Math.max(1, options.screenRenderScale ?? 1);
  const screenLayerSize = Math.max(size, Math.round(size * screenRenderScale));
  const activeMask = options.useAlternateMask && mockup.mask2 ? mockup.mask2 : mockup.mask;

  const master = createCanvas(size, size);
  const masterCtx = master.getContext('2d');
  if (!masterCtx) {
    throw new Error('2D context is not available for master canvas');
  }

  masterCtx.clearRect(0, 0, size, size);
  drawDiffuseLayer(masterCtx, mockup.base, activeMask, size, options.diffuse);

  if (userImage) {
    const quadSource =
      options.screenQuadOverride && options.screenQuadOverride.length === 4
        ? options.screenQuadOverride
        : mockup.template.screenQuad;
    const quad = scaleQuad(quadSource, screenLayerSize, outputSize);
    const warpMode = options.warpMode || (mockup.template.screenFit === 'contain' ? 'fit' : 'crop');
    const customTargetAspect = getCategoryAspect(mockup.template.category) ?? getQuadAspect(quad);
    const screenSource =
      warpMode === 'custom'
        ? createCustomPlacementSource(userImage, customTargetAspect, options.customPlacement)
        : userImage;
    const reflection = options.reflection;

    if (shouldRenderReflection(mockup, reflection)) {
      const reflectionQuadSource =
        options.reflectionQuadOverride && options.reflectionQuadOverride.length === 4
          ? options.reflectionQuadOverride
          : mockup.template.reflectionQuad || [];
      const reflectionQuad = scaleQuad(reflectionQuadSource, screenLayerSize, outputSize);
      const reflectionSource = createReflectedSource(screenSource);
      const reflectionLayer = createWarpedScreenLayer(reflectionSource, reflectionQuad, {
        size: screenLayerSize,
        gridX: options.gridX ?? 30,
        gridY: options.gridY ?? 30,
        fitMode: warpMode === 'custom' ? 'fit' : warpMode,
        maskImage: null
      });
      const reflectionBlur = Math.max(0, reflection?.blur ?? 16);
      const processedReflectionLayer = processReflectionLayer(
        reflectionLayer,
        mockup.reflectionMask,
        mockup.reflectionBlurMask,
        reflectionBlur
      );

      const reflectionOpacity = clamp((reflection?.opacity ?? 50) / 100, 0, 1);
      masterCtx.save();
      masterCtx.globalAlpha = reflectionOpacity;
      masterCtx.drawImage(processedReflectionLayer, 0, 0, screenLayerSize, screenLayerSize, 0, 0, size, size);
      masterCtx.restore();
    }

    // Warp without mask so applyScreenBlurMask can blur real content pixels at screen edges.
    // For the non-blur path the mask is applied in-place below.
    const warpedScreen = createWarpedScreenLayer(screenSource, quad, {
      size: screenLayerSize,
      gridX: options.gridX ?? 30,
      gridY: options.gridY ?? 30,
      fitMode: warpMode === 'custom' ? 'fit' : warpMode,
      maskImage: null
    });

    let screenCanvas: HTMLCanvasElement;
    // Static templates run this in preview too, otherwise the Screen blur slider
    // gives no feedback until export. Motion keeps the export-only guard: its
    // preview is a scrubbed video and the pyramid would cost too much per frame.
    const screenBlurInPreview = !mockup.template.isVideo;
    if (mockup.screenBlurMask && (!options.realtime || screenBlurInPreview)) {
      screenCanvas = applyScreenBlurMask(warpedScreen, activeMask, mockup.screenBlurMask);
    } else {
      applyMask(warpedScreen, activeMask);
      screenCanvas = warpedScreen;
    }
    masterCtx.drawImage(screenCanvas, 0, 0, screenLayerSize, screenLayerSize, 0, 0, size, size);
  }

  masterCtx.save();
  masterCtx.globalCompositeOperation = 'screen';
  masterCtx.drawImage(mockup.overlay, 0, 0, size, size);
  masterCtx.restore();

  if (mockup.template.transparency) {
    const alphaMask = createTransparencyMask(mockup.base, activeMask, size);
    masterCtx.save();
    masterCtx.globalCompositeOperation = 'destination-in';
    masterCtx.drawImage(alphaMask, 0, 0, size, size);
    masterCtx.restore();
  }

  applyBackgroundTreatment(master, masterCtx, mockup.backgroundMask, size, options.background);

  return master;
}

function shouldRenderReflection(
  mockup: LoadedMockupAssets,
  reflection?: ReflectionAdjustments
): boolean {
  return Boolean(
    mockup.template.reflectionEnabled &&
      reflection?.enabled !== false &&
      mockup.template.reflectionQuad &&
      mockup.template.reflectionQuad.length === 4
  );
}

function createWarpedScreenLayer(
  source: CanvasImageSource,
  quad: Point[],
  options: {
    size: number;
    gridX: number;
    gridY: number;
    fitMode: ScreenWarpMode;
    maskImage: CanvasImageSource | null;
  }
): HTMLCanvasElement {
  const screenCanvas = createCanvas(options.size, options.size);
  const screenCtx = screenCanvas.getContext('2d');
  if (!screenCtx) {
    throw new Error('2D context is not available for screen canvas');
  }

  warpImageToQuad(screenCtx, source, quad, {
    gridX: options.gridX,
    gridY: options.gridY,
    fitMode: options.fitMode,
    sourceMaxSize: options.size
  });

  healTransparentSeams(screenCanvas, 2);
  if (options.maskImage) {
    applyMask(screenCanvas, options.maskImage);
  }

  return screenCanvas;
}

/**
 * Applies a B&W blur mask to the user's screen canvas in-place.
 * White pixels in the mask = maximum blur on the screen content, blur bleeds
 * outside the cutout mask edges. Black = no blur. Skipped in realtime playback.
 */
/**
 * Blends between the sharp original and a blurred version using a single B&W mask.
 * canvas   – already-masked warped screen canvas (activeMask applied).
 * blurMask – atlas tile (0,1): black = sharp (no blur), white = maximum blur.
 * Returns a new canvas (does not modify the input).
 */
/**
 * Blends between the sharp original and a blurred version using a single B&W mask.
 * canvas   – already-masked warped screen canvas (activeMask applied).
 * blurMask – atlas tile (0,1): black = sharp (no blur), white = maximum blur.
 * Returns a new canvas (does not modify the input).
 */
function applyScreenBlurMask(
  unmaskedCanvas: HTMLCanvasElement,
  screenMask: CanvasImageSource,
  blurMask: CanvasImageSource,
  blurAmount: number = 50
): HTMLCanvasElement {
  const w = unmaskedCanvas.width;
  const h = unmaskedCanvas.height;
  // The slider is centred: 50 reproduces the radius this effect shipped with,
  // 100 doubles it. Same 0.1-100 range as the reflection blur slider.
  const strength = clamp(blurAmount, 0.1, 100) / 50;
  const baseBlurPx = Math.max(4, Math.round(Math.min(w, h) * 0.007));
  const maxBlurPx = Math.max(0.5, baseBlurPx * strength);

  // Geometric blur pyramid, same principle as processReflectionLayer.
  //
  // The old code cross-faded the SHARP source against a single fully blurred
  // copy. That is a dissolve, not a variable-radius blur: at a mid-grey mask
  // value half of the sharp image survives at half opacity, so edges stay
  // crisp and the result reads as haze. Only pure white looked right, because
  // only there did the sharp layer drop out completely.
  //
  // Mixing two ADJACENT blur levels instead means no sharp pixels are left
  // anywhere the mask asks for blur. Level 0 is the sharp source because black
  // on the mask must leave the content untouched — unlike reflections, whose
  // floor is 0.45R. The levels grow geometrically so neighbouring ones stay
  // close and each pairwise mix reads as a genuine intermediate blur.
  const { levels: pyramid, topPosition } = createBlurPyramid(unmaskedCanvas, maxBlurPx);
  if (pyramid.length === 0) {
    const fallback = cloneCanvas(unmaskedCanvas);
    applyMask(fallback, screenMask);
    return fallback;
  }

  const output = createCanvas(w, h);
  const outputCtx = output.getContext('2d');
  const sharpCtx = unmaskedCanvas.getContext('2d');
  if (!outputCtx || !sharpCtx) return unmaskedCanvas;

  const smCanvas = createCanvas(w, h);
  const bmCanvas = createCanvas(w, h);
  const smCtx = smCanvas.getContext('2d');
  const bmCtx = bmCanvas.getContext('2d');
  if (!smCtx || !bmCtx) return unmaskedCanvas;
  smCtx.drawImage(screenMask, 0, 0, w, h);
  bmCtx.drawImage(blurMask, 0, 0, w, h);

  const smData = smCtx.getImageData(0, 0, w, h);
  const bmData = bmCtx.getImageData(0, 0, w, h);

  // The accumulator starts at pyramid level 0 and walks up one level per pass.
  // Only two full-size pixel buffers are alive at a time — holding all five at
  // once costs ~160 MB at 2880px and is what used to crash iOS Safari tabs.
  const acc = sharpCtx.getImageData(0, 0, w, h);
  const accPixels = acc.data;

  for (let step = 0; step < pyramid.length; step++) {
    // The pyramid is built from the UNMASKED canvas so the blur averages real
    // content at the screen boundary instead of transparent zeros (which would
    // feather, not blur). Mobile levels arrive at work resolution and are only
    // upscaled here, one at a time, to keep peak memory at two full-size buffers.
    const layer = materializeBlurLevel(pyramid[step], unmaskedCanvas);
    const layerPixels = layer.getContext('2d')?.getImageData(0, 0, w, h).data;
    if (!layerPixels) continue;

    for (let i = 0; i < accPixels.length; i += 4) {
      const bmT = (bmData.data[i] + bmData.data[i + 1] + bmData.data[i + 2]) / (255 * 3) * (bmData.data[i + 3] / 255);
      const smoothT = bmT * bmT * (3 - 2 * bmT); // smoothstep
      const ramp = smoothT * topPosition - step;

      if (ramp <= 0) continue; // this pixel never reaches the current level

      if (ramp >= 1) {
        // Fully at or above this level: take it verbatim, no rounding error.
        accPixels[i] = layerPixels[i];
        accPixels[i + 1] = layerPixels[i + 1];
        accPixels[i + 2] = layerPixels[i + 2];
        continue;
      }

      const keep = 1 - ramp;
      accPixels[i] = accPixels[i] * keep + layerPixels[i] * ramp;
      accPixels[i + 1] = accPixels[i + 1] * keep + layerPixels[i + 1] * ramp;
      accPixels[i + 2] = accPixels[i + 2] * keep + layerPixels[i + 2] * ramp;
    }
  }

  // Alpha is never touched above, so it still holds the sharp silhouette here.
  // screenMask luminance cuts the screen shape out of it.
  for (let i = 3; i < accPixels.length; i += 4) {
    const smFactor = (smData.data[i - 3] + smData.data[i - 2] + smData.data[i - 1]) / (255 * 3) * (smData.data[i] / 255);
    accPixels[i] = accPixels[i] * smFactor;
  }

  outputCtx.putImageData(acc, 0, 0);
  return output;
}

function processReflectionLayer(
  reflectionCanvas: HTMLCanvasElement,
  visibilityMask: CanvasImageSource | null,
  blurMask: CanvasImageSource | null,
  blurAmount: number
): HTMLCanvasElement {
  // Fast path: skip all blur passes for negligible blur values.
  // resolveReflectionBlurPx clamps blurAmount to 0.1 minimum, so values < 5
  // produce < 3.75 px of real blur — effectively invisible at normal viewing sizes.
  if (blurAmount < 5) {
    const maskedSource = cloneCanvas(reflectionCanvas);
    if (visibilityMask) {
      applyMask(maskedSource, visibilityMask);
    }
    return maskedSource;
  }

  const maskedSource = cloneCanvas(reflectionCanvas);
  if (visibilityMask) {
    applyMask(maskedSource, visibilityMask);
  }

  const normalizedMaxBlur = resolveReflectionBlurPx(reflectionCanvas, blurAmount);
  const minimumBlur = normalizedMaxBlur > 0 ? Math.max(1.5, normalizedMaxBlur * 0.45) : 0;
  const baseLayer = minimumBlur > 0 ? createBlurredCanvas(maskedSource, minimumBlur) : maskedSource;
  const strongerBlurLayer =
    normalizedMaxBlur > minimumBlur ? createBlurredCanvas(maskedSource, normalizedMaxBlur) : baseLayer;
  const mixedLayer =
    blurMask && normalizedMaxBlur > minimumBlur
      ? blendReflectionBlurByMask(baseLayer, strongerBlurLayer, blurMask)
      : baseLayer;

  const transitionBlur = blurMask && normalizedMaxBlur > 0 ? Math.min(2, normalizedMaxBlur * 0.12) : 0;
  const softenedLayer = transitionBlur > 0 ? createBlurredCanvas(mixedLayer, transitionBlur) : mixedLayer;

  if (visibilityMask) {
    applyMask(softenedLayer, visibilityMask);
  }

  return softenedLayer;
}

function createReflectedSource(source: CanvasImageSource): HTMLCanvasElement {
  const { width, height } = getSourceSize(source);
  const canvas = createCanvas(width, height);
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('2D context is not available for reflection canvas');
  }

  ctx.save();
  ctx.translate(0, height);
  ctx.scale(1, -1);
  ctx.drawImage(source, 0, 0, width, height);
  ctx.restore();
  return canvas;
}

function createBlurredCanvas(source: HTMLCanvasElement, blurPx: number): HTMLCanvasElement {
  if (blurPx <= 0.01) {
    return cloneCanvas(source);
  }

  if (shouldUseApproximateCanvasBlur()) {
    return createApproximateBlurredCanvas(source, blurPx);
  }

  const canvas = createCanvas(source.width, source.height);
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return cloneCanvas(source);
  }

  ctx.save();
  ctx.filter = `blur(${Math.max(0, blurPx).toFixed(2)}px)`;
  ctx.drawImage(source, 0, 0);
  ctx.restore();

  // Keep the original alpha silhouette so blur affects detail, not overall visibility.
  ctx.save();
  ctx.globalCompositeOperation = 'destination-in';
  ctx.drawImage(source, 0, 0);
  ctx.restore();

  return canvas;
}

function shouldUseApproximateCanvasBlur(): boolean {
  return isWindowsChromiumCanvasRuntime() || isMobileCanvasRuntime();
}

function createApproximateBlurredCanvas(source: HTMLCanvasElement, blurPx: number): HTMLCanvasElement {
  if (isMobileCanvasRuntime()) {
    return createMobileApproximateBlurredCanvas(source, blurPx);
  }

  return createWindowsApproximateBlurredCanvas(source, blurPx);
}

function createWindowsApproximateBlurredCanvas(source: HTMLCanvasElement, blurPx: number): HTMLCanvasElement {
  const width = source.width;
  const height = source.height;
  const passes = blurPx >= 18 ? 3 : blurPx >= 8 ? 2 : 1;
  let current = source;

  for (let pass = 0; pass < passes; pass += 1) {
    const downscale = Math.max(1.25, Math.min(6, 1 + blurPx / 6 + pass * 0.35));
    current = createResampledBlurLayer(current, width, height, downscale, false);
  }

  return applySourceAlphaMask(current, source);
}

function createMobileApproximateBlurredCanvas(source: HTMLCanvasElement, blurPx: number): HTMLCanvasElement {
  const width = source.width;
  const height = source.height;
  const output = createCanvas(width, height);
  const outputCtx = output.getContext('2d');
  if (!outputCtx) {
    return cloneCanvas(source);
  }

  const normalizedBlur = Math.max(0.01, blurPx);
  const levels = buildMobileBlurLevels(normalizedBlur);
  const totalWeight = levels.reduce((sum, level) => sum + level.weight, 0) || 1;

  outputCtx.clearRect(0, 0, width, height);
  for (const level of levels) {
    const layer = createResampledBlurLayer(source, width, height, level.downscale, true);
    outputCtx.save();
    outputCtx.globalAlpha = level.weight / totalWeight;
    outputCtx.drawImage(layer, 0, 0);
    outputCtx.restore();
  }

  const polishDownscale = Math.max(1.08, Math.min(1.45, 1 + normalizedBlur / 42));
  const polished = createResampledBlurLayer(output, width, height, polishDownscale, true);
  outputCtx.save();
  outputCtx.globalAlpha = 0.38;
  outputCtx.drawImage(polished, 0, 0);
  outputCtx.restore();

  return applySourceAlphaMask(output, source);
}

function buildMobileBlurLevels(blurPx: number): Array<{ downscale: number; weight: number }> {
  const base = Math.max(1.18, Math.min(2.9, 1 + blurPx / 18));
  const levels: Array<{ downscale: number; weight: number }> = [
    { downscale: base, weight: 0.52 },
    { downscale: Math.max(1.3, Math.min(3.8, base * 1.28)), weight: 0.33 }
  ];

  if (blurPx >= 6) {
    levels.push({ downscale: Math.max(1.45, Math.min(4.8, base * 1.7)), weight: 0.15 });
  }

  if (blurPx >= 12) {
    levels.push({ downscale: Math.max(1.7, Math.min(5.6, base * 2.05)), weight: 0.08 });
  }

  return levels;
}

/**
 * Ascending-strength blur levels for applyScreenBlurMask, plus where the mask's
 * white point sits on that ladder.
 *
 * Desktop keeps independent Gaussians at radii that double each step.
 *
 * Mobile cannot use ctx.filter at all, so its only blur primitive is resolution
 * reduction. Measured on a step edge (sigma from the second moment of the edge
 * derivative), two things decide the design:
 *
 *  - Chaining resample passes at a fixed working size SATURATES. Six passes at
 *    downscale 1.5 only reach sigma 1.4px; the requested radius barely moves the
 *    result. That is why the old chain showed one flat blur with no gradient.
 *  - A single arbitrary downscale is quantised by the browser's internal mip
 *    chain: downscale 22.4x measured sigma 6.9 instead of 13, a 47% miss, while
 *    11.2x and 44.8x landed within 1%.
 *
 * Exact halving is the one step that behaves: sigma measured 0.70, 1.25, 2.50,
 * 4.98, 9.95, 19.91 for one through six halvings — a clean doubling per level,
 * which is exactly the geometric ramp this effect wants. So the mobile pyramid
 * IS the mip chain, and the mask's white point maps to a possibly fractional
 * position on it, letting the existing pairwise blend hit the requested radius.
 *
 * Levels stay at mip resolution — materialise them one at a time.
 * Reflections keep using createBlurredCanvas untouched; their look is signed off.
 */
interface BlurPyramid {
  /** Ascending blur strength. Index i is one step stronger than i-1. */
  levels: HTMLCanvasElement[];
  /** Level position the mask's white point maps to. May be fractional. */
  topPosition: number;
}

/**
 * Measured constant: mip level k lands on ~0.389 * 2^k px of blur at full
 * resolution, once materializeBlurLevel grows it back by doubling. (A direct
 * upscale gives 0.311 but shows facets — see materializeBlurLevel.)
 */
const MIP_SIGMA_BASE = 0.389;

function createBlurPyramid(source: HTMLCanvasElement, maxBlurPx: number): BlurPyramid {
  if (!shouldUseApproximateCanvasBlur()) {
    const levelCount = 4;
    const levels: HTMLCanvasElement[] = [];
    for (let level = 0; level < levelCount; level += 1) {
      levels.push(createBlurredCanvas(source, maxBlurPx / Math.pow(2, levelCount - 1 - level)));
    }
    return { levels, topPosition: levelCount };
  }

  // Halving six times already reaches ~20px of blur; a seventh step lands on a
  // canvas small enough (15px wide at 1920) that edge effects take over.
  const topPosition = clamp(Math.log2(Math.max(maxBlurPx, MIP_SIGMA_BASE * 2) / MIP_SIGMA_BASE), 1, 6);
  const levelCount = Math.ceil(topPosition);

  const levels: HTMLCanvasElement[] = [];
  let current = source;
  for (let level = 0; level < levelCount; level += 1) {
    const width = Math.max(2, Math.round(current.width / 2));
    const height = Math.max(2, Math.round(current.height / 2));
    const mip = createCanvas(width, height);
    const ctx = mip.getContext('2d');
    if (!ctx) {
      break;
    }
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(current, 0, 0, current.width, current.height, 0, 0, width, height);
    levels.push(mip);
    current = mip;
  }

  return { levels, topPosition: Math.min(topPosition, levels.length) };
}

/** Upscales a work-resolution pyramid level back to the source size, once it is needed. */
function materializeBlurLevel(level: HTMLCanvasElement, source: HTMLCanvasElement): HTMLCanvasElement {
  if (level.width === source.width && level.height === source.height) {
    return level;
  }

  // Grow back by doubling rather than in one jump. Bilinear upscaling straight
  // from a deep mip (30px wide at 1920) only guarantees C0 continuity, so the
  // reconstruction shows the tent patches as blurry quadrilateral facets — the
  // "squares" visible at strong blur. Each 2x step keeps the interpolation local
  // and composes into a far smoother reconstruction.
  //
  // Measured side effect: doubling adds ~25% sigma versus a direct jump
  // (4.98 -> 6.20, 9.95 -> 12.44, 19.91 -> 24.95). The ratio between levels stays
  // 2.006, so the ladder is still geometric — only MIP_SIGMA_BASE moves.
  let current = level;
  while (current.width * 2 < source.width) {
    const width = Math.min(source.width, current.width * 2);
    const height = Math.min(source.height, Math.max(1, current.height * 2));
    const step = createCanvas(width, height);
    const stepCtx = step.getContext('2d');
    if (!stepCtx) {
      break;
    }
    stepCtx.imageSmoothingEnabled = true;
    stepCtx.imageSmoothingQuality = 'high';
    stepCtx.drawImage(current, 0, 0, current.width, current.height, 0, 0, width, height);
    current = step;
  }

  const out = createCanvas(source.width, source.height);
  const ctx = out.getContext('2d');
  if (!ctx) {
    return level;
  }

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(current, 0, 0, current.width, current.height, 0, 0, source.width, source.height);
  ctx.save();
  ctx.globalCompositeOperation = 'destination-in';
  ctx.drawImage(source, 0, 0);
  ctx.restore();
  return out;
}

function createResampledBlurLayer(
  source: HTMLCanvasElement,
  width: number,
  height: number,
  downscale: number,
  highQuality: boolean
): HTMLCanvasElement {
  const clampedDownscale = Math.max(1.01, downscale);
  const smallWidth = Math.max(1, Math.round(width / clampedDownscale));
  const smallHeight = Math.max(1, Math.round(height / clampedDownscale));
  const downsampled = createCanvas(smallWidth, smallHeight);
  const downsampledCtx = downsampled.getContext('2d');
  const upsampled = createCanvas(width, height);
  const upsampledCtx = upsampled.getContext('2d');

  if (!downsampledCtx || !upsampledCtx) {
    return cloneCanvas(source);
  }

  const smoothing: ImageSmoothingQuality = highQuality ? 'high' : 'medium';
  downsampledCtx.imageSmoothingEnabled = true;
  downsampledCtx.imageSmoothingQuality = smoothing;
  upsampledCtx.imageSmoothingEnabled = true;
  upsampledCtx.imageSmoothingQuality = smoothing;

  downsampledCtx.clearRect(0, 0, smallWidth, smallHeight);
  downsampledCtx.drawImage(source, 0, 0, width, height, 0, 0, smallWidth, smallHeight);
  upsampledCtx.clearRect(0, 0, width, height);
  upsampledCtx.drawImage(downsampled, 0, 0, smallWidth, smallHeight, 0, 0, width, height);
  return upsampled;
}

function applySourceAlphaMask(layer: HTMLCanvasElement, source: HTMLCanvasElement): HTMLCanvasElement {
  const output = createCanvas(layer.width, layer.height);
  const outputCtx = output.getContext('2d');
  if (!outputCtx) {
    return cloneCanvas(layer);
  }

  outputCtx.drawImage(layer, 0, 0);
  outputCtx.save();
  outputCtx.globalCompositeOperation = 'destination-in';
  outputCtx.drawImage(source, 0, 0);
  outputCtx.restore();
  return output;
}

function resolveReflectionBlurPx(reflectionCanvas: HTMLCanvasElement, blurAmount: number): number {
  const normalizedAmount = clamp(blurAmount, 0.1, 100) / 100;
  const maxBlurPx = Math.max(12, Math.min(reflectionCanvas.width, reflectionCanvas.height) * (150 / 3840));
  return normalizedAmount * maxBlurPx;
}

function blendReflectionBlurByMask(
  lessBlurredLayer: HTMLCanvasElement,
  moreBlurredLayer: HTMLCanvasElement,
  blurMask: CanvasImageSource
): HTMLCanvasElement {
  const width = lessBlurredLayer.width;
  const height = lessBlurredLayer.height;
  const output = createCanvas(width, height);
  const outputCtx = output.getContext('2d');
  if (!outputCtx) {
    return moreBlurredLayer;
  }

  const lessBlurredCtx = lessBlurredLayer.getContext('2d');
  const moreBlurredCtx = moreBlurredLayer.getContext('2d');
  if (!lessBlurredCtx || !moreBlurredCtx) {
    return moreBlurredLayer;
  }

  const maskCanvas = createCanvas(width, height);
  const maskCtx = maskCanvas.getContext('2d');
  if (!maskCtx) {
    return moreBlurredLayer;
  }

  maskCtx.drawImage(blurMask, 0, 0, width, height);

  const lessBlurredData = lessBlurredCtx.getImageData(0, 0, width, height);
  const moreBlurredData = moreBlurredCtx.getImageData(0, 0, width, height);
  const maskData = maskCtx.getImageData(0, 0, width, height);
  const outputData = outputCtx.createImageData(width, height);

  for (let index = 0; index < outputData.data.length; index += 4) {
    const maskAlpha = maskData.data[index + 3] / 255;
    const maskLuminance =
      (maskData.data[index] + maskData.data[index + 1] + maskData.data[index + 2]) / (255 * 3);
    const normalizedMix = clamp(maskLuminance * maskAlpha, 0, 1);
    const mix = normalizedMix * normalizedMix * (3 - 2 * normalizedMix);

    outputData.data[index] = Math.round(
      lessBlurredData.data[index] * (1 - mix) + moreBlurredData.data[index] * mix
    );
    outputData.data[index + 1] = Math.round(
      lessBlurredData.data[index + 1] * (1 - mix) + moreBlurredData.data[index + 1] * mix
    );
    outputData.data[index + 2] = Math.round(
      lessBlurredData.data[index + 2] * (1 - mix) + moreBlurredData.data[index + 2] * mix
    );
    outputData.data[index + 3] = Math.round(
      lessBlurredData.data[index + 3] * (1 - mix) + moreBlurredData.data[index + 3] * mix
    );
  }

  outputCtx.putImageData(outputData, 0, 0);
  return output;
}

function cloneCanvas(source: HTMLCanvasElement): HTMLCanvasElement {
  const clone = createCanvas(source.width, source.height);
  const ctx = clone.getContext('2d');
  if (!ctx) {
    return clone;
  }
  ctx.drawImage(source, 0, 0);
  return clone;
}

function getSourceSize(source: CanvasImageSource): { width: number; height: number } {
  if (source instanceof HTMLVideoElement) {
    return {
      width: Math.max(1, source.videoWidth || source.width || 1),
      height: Math.max(1, source.videoHeight || source.height || 1)
    };
  }

  if (source instanceof HTMLImageElement) {
    return {
      width: Math.max(1, source.naturalWidth || source.width || 1),
      height: Math.max(1, source.naturalHeight || source.height || 1)
    };
  }

  const canvasLike = source as HTMLCanvasElement | ImageBitmap | OffscreenCanvas | VideoFrame;
  const width = 'displayWidth' in canvasLike ? canvasLike.displayWidth : canvasLike.width;
  const height = 'displayHeight' in canvasLike ? canvasLike.displayHeight : canvasLike.height;

  return {
    width: Math.max(1, Number(width) || 1),
    height: Math.max(1, Number(height) || 1)
  };
}

function drawDiffuseLayer(
  ctx: CanvasRenderingContext2D,
  image: CanvasImageSource,
  mask: CanvasImageSource,
  size: number,
  adjustments?: DiffuseAdjustments
): void {
  ctx.drawImage(image, 0, 0, size, size);

  if (!adjustments || isNeutralDiffuseAdjustments(adjustments)) {
    return;
  }

  const adjustedCanvas = createCanvas(size, size);
  const adjustedCtx = adjustedCanvas.getContext('2d');
  if (!adjustedCtx) {
    return;
  }

  drawImageWithDiffuseAdjustments(adjustedCtx, image, adjustments, null, {
    dx: 0,
    dy: 0,
    dw: size,
    dh: size
  });

  const outsideMaskCanvas = createOutsideScreenMask(mask, size);
  adjustedCtx.save();
  adjustedCtx.globalCompositeOperation = 'destination-in';
  adjustedCtx.drawImage(outsideMaskCanvas, 0, 0, size, size);
  adjustedCtx.restore();

  ctx.drawImage(adjustedCanvas, 0, 0, size, size);
}

export function drawImageWithDiffuseAdjustments(
  ctx: CanvasRenderingContext2D,
  image: CanvasImageSource,
  adjustments: DiffuseAdjustments | undefined,
  sourceRect: ImageSourceRect | null,
  destRect: ImageDestRect
): void {
  if (!adjustments || isNeutralDiffuseAdjustments(adjustments)) {
    drawImageRect(ctx, image, sourceRect, destRect);
    return;
  }

  if (shouldUseCanvasDiffuseFilter()) {
    ctx.save();
    ctx.filter = createDiffuseFilter(adjustments);
    drawImageRect(ctx, image, sourceRect, destRect);
    ctx.restore();
    return;
  }

  const patchWidth = Math.max(1, Math.round(destRect.dw));
  const patchHeight = Math.max(1, Math.round(destRect.dh));
  const patchCanvas = createCanvas(patchWidth, patchHeight);
  const patchCtx = patchCanvas.getContext('2d');
  if (!patchCtx) {
    drawImageRect(ctx, image, sourceRect, destRect);
    return;
  }

  patchCtx.imageSmoothingEnabled = ctx.imageSmoothingEnabled;
  patchCtx.imageSmoothingQuality = ctx.imageSmoothingQuality;
  drawImageRect(patchCtx, image, sourceRect, {
    dx: 0,
    dy: 0,
    dw: patchWidth,
    dh: patchHeight
  });

  if (!applyDiffusePixelAdjustments(patchCtx, patchWidth, patchHeight, adjustments)) {
    drawImageRect(ctx, image, sourceRect, destRect);
    return;
  }

  ctx.drawImage(patchCanvas, destRect.dx, destRect.dy, destRect.dw, destRect.dh);
}

function applyBackgroundTreatment(
  targetCanvas: HTMLCanvasElement,
  ctx: CanvasRenderingContext2D,
  backgroundMask: CanvasImageSource | null,
  size: number,
  adjustments?: BackgroundAdjustments
): void {
  if (!backgroundMask || !adjustments || isNeutralBackgroundAdjustments(adjustments)) {
    return;
  }

  const maskCanvas = createMaskCoverageCanvas(backgroundMask, size);

  if (adjustments.transparent) {
    ctx.save();
    ctx.globalCompositeOperation = 'destination-out';
    ctx.drawImage(maskCanvas, 0, 0, size, size);
    ctx.restore();
    return;
  }

  const sourceCanvas = createCanvas(size, size);
  const sourceCtx = sourceCanvas.getContext('2d');
  const maskCtx = maskCanvas.getContext('2d');
  if (!sourceCtx || !maskCtx) {
    return;
  }

  sourceCtx.drawImage(targetCanvas, 0, 0);
  const sourceData = sourceCtx.getImageData(0, 0, size, size);
  const maskData = maskCtx.getImageData(0, 0, size, size);

  const recoloredCanvas = createCanvas(size, size);
  const recoloredCtx = recoloredCanvas.getContext('2d');
  if (!recoloredCtx) {
    return;
  }

  const recolored = recoloredCtx.createImageData(size, size);
  const sourcePixels = sourceData.data;
  const maskPixels = maskData.data;
  const outPixels = recolored.data;

  const dark = parseHexColor(adjustments.shadowColor);
  const mid = parseHexColor(adjustments.midColor);
  const light = parseHexColor(adjustments.highlightColor);

  for (let index = 0; index < outPixels.length; index += 4) {
    const maskCoverage = maskPixels[index + 3] / 255;
    if (maskCoverage <= 0) {
      continue;
    }

    const sourceAlpha = sourcePixels[index + 3] / 255;
    if (sourceAlpha <= 0) {
      continue;
    }

    const luminance = getPerceivedLuminance(sourcePixels[index], sourcePixels[index + 1], sourcePixels[index + 2]);
    const mappedColor =
      adjustments.mode === 'tritone'
        ? sampleTritoneColor(dark, mid, light, luminance)
        : lerpColor(dark, light, luminance);

    outPixels[index] = mappedColor.r;
    outPixels[index + 1] = mappedColor.g;
    outPixels[index + 2] = mappedColor.b;
    outPixels[index + 3] = Math.round(sourceAlpha * maskCoverage * 255);
  }

  recoloredCtx.putImageData(recolored, 0, 0);
  ctx.drawImage(recoloredCanvas, 0, 0);
}

function createDiffuseFilter(adjustments: DiffuseAdjustments): string {
  return [
    `hue-rotate(${adjustments.hue}deg)`,
    `saturate(${adjustments.saturation}%)`,
    `brightness(${adjustments.brightness}%)`,
    `contrast(${adjustments.contrast}%)`,
    adjustments.monochrome ? 'grayscale(100%)' : 'grayscale(0%)'
  ].join(' ');
}

let canvasDiffuseFilterSupported: boolean | null = null;

function shouldUseCanvasDiffuseFilter(): boolean {
  if (isSafariCanvasRuntime() || isWindowsChromiumCanvasRuntime()) {
    return false;
  }

  return supportsCanvasDiffuseFilter();
}

function isSafariCanvasRuntime(): boolean {
  if (typeof navigator === 'undefined') {
    return false;
  }

  const userAgent = navigator.userAgent || '';
  const vendor = navigator.vendor || '';
  const isAppleSafari = /Safari/i.test(userAgent) && /Apple/i.test(vendor);
  const isChromiumOrFirefoxShell = /Chrome|CriOS|Chromium|Edg|OPR|FxiOS|Android/i.test(userAgent);
  return isAppleSafari && !isChromiumOrFirefoxShell;
}

function isWindowsChromiumCanvasRuntime(): boolean {
  if (typeof navigator === 'undefined') {
    return false;
  }

  const userAgent = navigator.userAgent || '';
  const platform = navigator.platform || '';
  const isWindows = /Win/i.test(platform) || /Windows/i.test(userAgent);
  const isChromiumShell = /Chrome|Chromium|CriOS|Edg|OPR/i.test(userAgent);
  const isFirefoxShell = /Firefox|FxiOS/i.test(userAgent);
  return isWindows && isChromiumShell && !isFirefoxShell;
}

function isMobileCanvasRuntime(): boolean {
  if (typeof navigator === 'undefined') {
    return false;
  }

  const userAgent = navigator.userAgent || '';
  return /Android|iPhone|iPad|iPod|Mobile/i.test(userAgent);
}

function supportsCanvasDiffuseFilter(): boolean {
  if (canvasDiffuseFilterSupported !== null) {
    return canvasDiffuseFilterSupported;
  }

  const sourceCanvas = createCanvas(1, 1);
  const sourceCtx = sourceCanvas.getContext('2d');
  const targetCanvas = createCanvas(1, 1);
  const targetCtx = targetCanvas.getContext('2d');
  if (!sourceCtx || !targetCtx || !('filter' in targetCtx)) {
    canvasDiffuseFilterSupported = false;
    return canvasDiffuseFilterSupported;
  }

  try {
    sourceCtx.fillStyle = 'rgb(255, 0, 0)';
    sourceCtx.fillRect(0, 0, 1, 1);
    targetCtx.filter = 'hue-rotate(120deg) saturate(130%) brightness(90%) contrast(110%)';
    targetCtx.drawImage(sourceCanvas, 0, 0);
    targetCtx.filter = 'none';
    const pixel = targetCtx.getImageData(0, 0, 1, 1).data;
    canvasDiffuseFilterSupported = !(pixel[0] > 240 && pixel[1] < 10 && pixel[2] < 10);
  } catch {
    canvasDiffuseFilterSupported = false;
  }

  return canvasDiffuseFilterSupported;
}

function drawImageRect(
  ctx: CanvasRenderingContext2D,
  image: CanvasImageSource,
  sourceRect: ImageSourceRect | null,
  destRect: ImageDestRect
): void {
  if (sourceRect) {
    ctx.drawImage(
      image,
      sourceRect.sx,
      sourceRect.sy,
      sourceRect.sw,
      sourceRect.sh,
      destRect.dx,
      destRect.dy,
      destRect.dw,
      destRect.dh
    );
    return;
  }

  ctx.drawImage(image, destRect.dx, destRect.dy, destRect.dw, destRect.dh);
}

function applyDiffusePixelAdjustments(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  adjustments: DiffuseAdjustments
): boolean {
  try {
    const imageData = ctx.getImageData(0, 0, width, height);
    const pixels = imageData.data;
    const hueRadians = (adjustments.hue * Math.PI) / 180;
    const hueCos = Math.cos(hueRadians);
    const hueSin = Math.sin(hueRadians);
    const saturation = Math.max(0, adjustments.saturation / 100);
    const brightness = Math.max(0, adjustments.brightness / 100);
    const contrast = Math.max(0, adjustments.contrast / 100);

    for (let index = 0; index < pixels.length; index += 4) {
      if (pixels[index + 3] === 0) {
        continue;
      }

      let r = pixels[index];
      let g = pixels[index + 1];
      let b = pixels[index + 2];

      if (adjustments.hue !== 0) {
        [r, g, b] = applyHueRotate(r, g, b, hueCos, hueSin);
      }

      if (saturation !== 1) {
        [r, g, b] = applySaturate(r, g, b, saturation);
      }

      if (brightness !== 1) {
        r *= brightness;
        g *= brightness;
        b *= brightness;
      }

      if (contrast !== 1) {
        r = (r - 128) * contrast + 128;
        g = (g - 128) * contrast + 128;
        b = (b - 128) * contrast + 128;
      }

      if (adjustments.monochrome) {
        [r, g, b] = applySaturate(r, g, b, 0);
      }

      pixels[index] = clampByte(r);
      pixels[index + 1] = clampByte(g);
      pixels[index + 2] = clampByte(b);
    }

    ctx.putImageData(imageData, 0, 0);
    return true;
  } catch {
    return false;
  }
}

function applyHueRotate(
  r: number,
  g: number,
  b: number,
  cos: number,
  sin: number
): [number, number, number] {
  return [
    (0.213 + cos * 0.787 - sin * 0.213) * r +
      (0.715 - cos * 0.715 - sin * 0.715) * g +
      (0.072 - cos * 0.072 + sin * 0.928) * b,
    (0.213 - cos * 0.213 + sin * 0.143) * r +
      (0.715 + cos * 0.285 + sin * 0.14) * g +
      (0.072 - cos * 0.072 - sin * 0.283) * b,
    (0.213 - cos * 0.213 - sin * 0.787) * r +
      (0.715 - cos * 0.715 + sin * 0.715) * g +
      (0.072 + cos * 0.928 + sin * 0.072) * b
  ];
}

function applySaturate(r: number, g: number, b: number, saturation: number): [number, number, number] {
  return [
    (0.213 + 0.787 * saturation) * r + (0.715 - 0.715 * saturation) * g + (0.072 - 0.072 * saturation) * b,
    (0.213 - 0.213 * saturation) * r + (0.715 + 0.285 * saturation) * g + (0.072 - 0.072 * saturation) * b,
    (0.213 - 0.213 * saturation) * r + (0.715 - 0.715 * saturation) * g + (0.072 + 0.928 * saturation) * b
  ];
}

function clampByte(value: number): number {
  return Math.max(0, Math.min(255, Math.round(value)));
}

function isNeutralDiffuseAdjustments(adjustments: DiffuseAdjustments): boolean {
  return (
    adjustments.hue === 0 &&
    adjustments.saturation === 100 &&
    adjustments.brightness === 100 &&
    adjustments.contrast === 100 &&
    !adjustments.monochrome
  );
}

function isNeutralBackgroundAdjustments(adjustments: BackgroundAdjustments): boolean {
  return !adjustments.transparent && adjustments.mode === 'original';
}

function createOutsideScreenMask(mask: CanvasImageSource, size: number): HTMLCanvasElement {
  const maskCanvas = createCanvas(size, size);
  const maskCtx = maskCanvas.getContext('2d');
  if (!maskCtx) {
    return maskCanvas;
  }

  maskCtx.clearRect(0, 0, size, size);
  maskCtx.drawImage(mask, 0, 0, size, size);

  const imageData = maskCtx.getImageData(0, 0, size, size);
  const pixels = imageData.data;

  for (let index = 0; index < pixels.length; index += 4) {
    const r = pixels[index];
    const g = pixels[index + 1];
    const b = pixels[index + 2];
    const a = pixels[index + 3] / 255;
    const luminance = ((r + g + b) / (255 * 3)) * a;
    const outsideAlpha = Math.round((1 - luminance) * 255);

    pixels[index] = 255;
    pixels[index + 1] = 255;
    pixels[index + 2] = 255;
    pixels[index + 3] = outsideAlpha;
  }

  maskCtx.putImageData(imageData, 0, 0);
  return maskCanvas;
}

function createMaskCoverageCanvas(mask: CanvasImageSource, size: number): HTMLCanvasElement {
  const maskCanvas = createCanvas(size, size);
  const maskCtx = maskCanvas.getContext('2d');
  if (!maskCtx) {
    return maskCanvas;
  }

  maskCtx.clearRect(0, 0, size, size);
  maskCtx.drawImage(mask, 0, 0, size, size);

  const imageData = maskCtx.getImageData(0, 0, size, size);
  const pixels = imageData.data;

  for (let index = 0; index < pixels.length; index += 4) {
    const luminance = getPerceivedLuminance(pixels[index], pixels[index + 1], pixels[index + 2]);
    // Background mask semantics: white keeps foreground objects, dark marks removable/recolorable background.
    const alpha = Math.round((1 - luminance) * (pixels[index + 3] / 255) * 255);
    pixels[index] = 255;
    pixels[index + 1] = 255;
    pixels[index + 2] = 255;
    pixels[index + 3] = alpha;
  }

  maskCtx.putImageData(imageData, 0, 0);
  return maskCanvas;
}

function createCustomPlacementSource(
  image: CanvasImageSource,
  targetAspect: number,
  placement?: CustomPlacement | null
): HTMLCanvasElement {
  const { width: imageWidth, height: imageHeight } = getSourceSize(image);
  const base = Math.max(imageWidth, imageHeight, 1024);

  let width: number;
  let height: number;
  if (targetAspect >= 1) {
    width = base;
    height = Math.max(1, Math.round(base / targetAspect));
  } else {
    height = base;
    width = Math.max(1, Math.round(base * targetAspect));
  }

  const canvas = createCanvas(width, height);
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('2D context is not available for custom placement canvas');
  }

  ctx.clearRect(0, 0, width, height);

  const coverScale = Math.max(width / imageWidth, height / imageHeight);
  const normalizedPlacement = normalizeCustomPlacement(width, height, imageWidth, imageHeight, {
    scale: placement?.scale ?? 1,
    offsetX: placement?.offsetX ?? 0,
    offsetY: placement?.offsetY ?? 0
  });

  const drawWidth = imageWidth * coverScale * normalizedPlacement.scale;
  const drawHeight = imageHeight * coverScale * normalizedPlacement.scale;
  const centerX = width / 2 + normalizedPlacement.offsetX * width;
  const centerY = height / 2 + normalizedPlacement.offsetY * height;
  const dx = centerX - drawWidth / 2;
  const dy = centerY - drawHeight / 2;

  ctx.drawImage(image, dx, dy, drawWidth, drawHeight);
  return canvas;
}

function normalizeCustomPlacement(
  viewportWidth: number,
  viewportHeight: number,
  imageWidth: number,
  imageHeight: number,
  placement: CustomPlacement
): CustomPlacement {
  const safeViewportWidth =
    Number.isFinite(viewportWidth) && viewportWidth > 0 ? viewportWidth : 1;
  const safeViewportHeight =
    Number.isFinite(viewportHeight) && viewportHeight > 0 ? viewportHeight : 1;
  const safeImageWidth = Number.isFinite(imageWidth) && imageWidth > 0 ? imageWidth : 1;
  const safeImageHeight = Number.isFinite(imageHeight) && imageHeight > 0 ? imageHeight : 1;
  const scale = clamp(Number.isFinite(placement.scale) ? placement.scale : 1, 1, 6);
  const offsetX = Number.isFinite(placement.offsetX) ? placement.offsetX : 0;
  const offsetY = Number.isFinite(placement.offsetY) ? placement.offsetY : 0;
  const coverScale = Math.max(
    safeViewportWidth / safeImageWidth,
    safeViewportHeight / safeImageHeight
  );
  const drawWidth = safeImageWidth * coverScale * scale;
  const drawHeight = safeImageHeight * coverScale * scale;
  const maxOffsetX =
    Math.max(0, (drawWidth - safeViewportWidth) / 2) / safeViewportWidth;
  const maxOffsetY =
    Math.max(0, (drawHeight - safeViewportHeight) / 2) / safeViewportHeight;

  return {
    scale,
    offsetX: clamp(offsetX, -maxOffsetX, maxOffsetX),
    offsetY: clamp(offsetY, -maxOffsetY, maxOffsetY)
  };
}

function getQuadAspect(quad: Point[]): number {
  const top = edgeLength(quad[0], quad[1]);
  const bottom = edgeLength(quad[3], quad[2]);
  const left = edgeLength(quad[0], quad[3]);
  const right = edgeLength(quad[1], quad[2]);
  const width = (top + bottom) / 2;
  const height = (left + right) / 2;
  const aspect = width / Math.max(1e-6, height);

  if (!Number.isFinite(aspect) || aspect <= 0) {
    return 1;
  }

  return Math.max(0.05, Math.min(20, aspect));
}

function edgeLength(a: Point, b: Point): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  return Math.hypot(dx, dy);
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function createTransparencyMask(
  baseImage: CanvasImageSource,
  screenMaskImage: CanvasImageSource,
  size: number
): HTMLCanvasElement {
  const baseCanvas = createCanvas(size, size);
  const baseCtx = baseCanvas.getContext('2d');
  if (!baseCtx) {
    return baseCanvas;
  }
  baseCtx.clearRect(0, 0, size, size);
  baseCtx.drawImage(baseImage, 0, 0, size, size);
  const baseData = baseCtx.getImageData(0, 0, size, size);

  const maskCanvas = createCanvas(size, size);
  const maskCtx = maskCanvas.getContext('2d');
  if (!maskCtx) {
    return baseCanvas;
  }
  maskCtx.clearRect(0, 0, size, size);
  maskCtx.drawImage(screenMaskImage, 0, 0, size, size);
  const screenMaskData = maskCtx.getImageData(0, 0, size, size);

  const output = createCanvas(size, size);
  const outputCtx = output.getContext('2d');
  if (!outputCtx) {
    return baseCanvas;
  }

  const out = outputCtx.createImageData(size, size);
  const pixels = out.data;

  for (let index = 0; index < pixels.length; index += 4) {
    const baseAlpha = baseData.data[index + 3];

    const maskR = screenMaskData.data[index];
    const maskG = screenMaskData.data[index + 1];
    const maskB = screenMaskData.data[index + 2];
    const maskA = screenMaskData.data[index + 3] / 255;
    const screenAlpha = Math.round(((maskR + maskG + maskB) / (255 * 3)) * maskA * 255);

    const alpha = Math.max(baseAlpha, screenAlpha);
    pixels[index] = 255;
    pixels[index + 1] = 255;
    pixels[index + 2] = 255;
    pixels[index + 3] = alpha;
  }

  outputCtx.putImageData(out, 0, 0);
  return output;
}

function healTransparentSeams(canvas: HTMLCanvasElement, radius: number): void {
  const ctx = canvas.getContext('2d');
  if (!ctx || radius <= 0) {
    return;
  }

  const copy = createCanvas(canvas.width, canvas.height);
  const copyCtx = copy.getContext('2d');
  if (!copyCtx) {
    return;
  }
  copyCtx.drawImage(canvas, 0, 0);

  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(copy, 0, 0);

  ctx.save();
  ctx.globalCompositeOperation = 'destination-over';
  for (let dy = -radius; dy <= radius; dy += 1) {
    for (let dx = -radius; dx <= radius; dx += 1) {
      if (dx === 0 && dy === 0) {
        continue;
      }
      ctx.drawImage(copy, dx, dy);
    }
  }
  ctx.restore();
}

function scaleQuad(
  quad: MockupTemplate['screenQuad'],
  targetSize: number,
  sourceSize: number
): MockupTemplate['screenQuad'] {
  if (sourceSize === targetSize) {
    return quad;
  }

  const factor = targetSize / sourceSize;
  return quad.map((point) => ({
    // Preserve subpixel precision when rendering/exporting to a different size.
    // Rounding here causes visible stepping in motion warp at 1920 -> 1080.
    x: point.x * factor,
    y: point.y * factor
  }));
}

function parseHexColor(value: string): { r: number; g: number; b: number } {
  const normalized = value.trim().replace('#', '');
  const hex = normalized.length === 3
    ? normalized
        .split('')
        .map((char) => char + char)
        .join('')
    : normalized.padEnd(6, '0').slice(0, 6);

  const parsed = Number.parseInt(hex, 16);
  if (!Number.isFinite(parsed)) {
    return { r: 255, g: 255, b: 255 };
  }

  return {
    r: (parsed >> 16) & 255,
    g: (parsed >> 8) & 255,
    b: parsed & 255
  };
}

function lerpColor(
  from: { r: number; g: number; b: number },
  to: { r: number; g: number; b: number },
  amount: number
): { r: number; g: number; b: number } {
  const t = clamp01(amount);
  return {
    r: Math.round(from.r + (to.r - from.r) * t),
    g: Math.round(from.g + (to.g - from.g) * t),
    b: Math.round(from.b + (to.b - from.b) * t)
  };
}

function sampleTritoneColor(
  shadow: { r: number; g: number; b: number },
  mid: { r: number; g: number; b: number },
  highlight: { r: number; g: number; b: number },
  amount: number
): { r: number; g: number; b: number } {
  const t = clamp01(amount);
  if (t <= 0.5) {
    return lerpColor(shadow, mid, t / 0.5);
  }
  return lerpColor(mid, highlight, (t - 0.5) / 0.5);
}

function getPerceivedLuminance(r: number, g: number, b: number): number {
  return clamp01((0.2126 * r + 0.7152 * g + 0.0722 * b) / 255);
}

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value));
}
